/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import React, { Fragment, useRef } from 'react';
import { DatePicker, Input, message, Modal, Form, Button } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useHistory } from 'react-router-dom';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { addTender } from '~src/services/api';
import { Controller, useForm } from 'react-hook-form';
import TextArea from 'antd/lib/input/TextArea';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './CreateTenderModal.style';
import moment from 'moment';
import IconButton from '~components/_common/IconButton/IconButton';

export type CreateTenderModalProps = Pick<ModalProps, 'visible'> & {
  onCancel?: () => void;
  projectId?: number;
};

export const CreateTenderModal: React.FC<CreateTenderModalProps> = ({
  onCancel,
  projectId,
  ...modalProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();

  const submitActionRef = useRef<'stay' | 'open'>('stay');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      title: '',
      share_title: '',
      description: '',
      reference: '',
      deadline_at: moment().add(3, 'months'),
    },
  });

  const [addTenderMutation, { isLoading: isAddTenderLoading }] = useMutation(
    addTender,
    {
      onSuccess: (createdTender: any) => {
        queryCache.invalidateQueries([QueryKeys.sharedTenderQuestion]);
        queryCache.invalidateQueries([QueryKeys.question]);
        queryCache.invalidateQueries([
          'tenders',
          { projectId: String(projectId) },
        ]);

        if (
          submitActionRef.current === 'open' &&
          createdTender?.id &&
          projectId
        ) {
          history.push(`/projects/${projectId}/tenders/${createdTender.id}`);
        }

        reset();
        onCancel?.();
      },
      onError: (error, item) => {
        message.error(
          intl.formatMessage({ id: 'createTenderModal-error-failed' })
        );
        appLog('addTenderMutation -> item', item);
        appLog('addTenderMutation -> err', error);
      },
    }
  );

  const onSubmit = (data: any) => {
    try {
      if (!projectId) return;

      data.reference = stripReferenceHash(data.reference);

      if (!data.share_title?.trim()) {
        delete data.share_title;
      }

      addTenderMutation({
        data: {
          ...data,
          deadline_at: data.deadline_at.unix(),
        },
        projectId: String(projectId),
      });
    } catch (err: any) {
      message.error(err.message || 'Failed to create tender');
    }
  };

  const disabledDate = (current) =>
    current && current < moment().startOf('day');

  // Refactored to accept a key for unique help strings per field
  const IconPopup = ({ translationKey }: { translationKey: string }) => (
    <IconButton
      icon={['far', 'info-circle']}
      containerStyle={{ marginLeft: 0, color: '#7a829b' }}
      backgroundOff
      tooltip={
        <div>
          <h3 style={{ color: 'white' }}>
            {intl.formatMessage({
              id: `createTenderModal-help-${translationKey}-title`,
            })}
          </h3>
          <p>
            {intl.formatMessage({
              id: `createTenderModal-help-${translationKey}-desc`,
            })}
          </p>
        </div>
      }
      widthTooltip="300px"
    />
  );

  const handleClose = () => {
    reset();
    onCancel?.();
  };

  const stripReferenceHash = (value: any) =>
    String(value ?? '')
      .trim()
      .replace(/^#+/, '');

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'createTenderModal-header' })}
          icon={['far', 'question-circle']}
        />
      }
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      onCancel={handleClose}
      destroyOnClose
      width={700}
      footer={[
        <Button
          key="cancel"
          onClick={handleClose}
          disabled={isAddTenderLoading}>
          {intl.formatMessage({ id: 'cancel' })}
        </Button>,

        <Button
          key="add"
          type="default"
          loading={isAddTenderLoading}
          onClick={() => {
            submitActionRef.current = 'stay';
            handleSubmit(onSubmit)();
          }}>
          {intl.formatMessage({ id: 'add' })}
        </Button>,

        <Button
          key="add-open"
          type="primary"
          loading={isAddTenderLoading}
          onClick={() => {
            submitActionRef.current = 'open';
            handleSubmit(onSubmit)();
          }}>
          {intl.formatMessage({ id: 'add-and-open' })}
        </Button>,
      ]}
      {...modalProps}>
      <Form layout="vertical" css={styles.formContainer}>
        <Form.Item
          label={
            <Fragment>
              {intl.formatMessage({ id: 'createTenderModal-reference' })}{' '}
              <IconPopup translationKey="reference" />
            </Fragment>
          }
          validateStatus={errors.reference ? 'error' : ''}
          help={
            errors.reference &&
            intl.formatMessage({ id: 'createTenderModal-field-required' })
          }>
          <Controller
            name="reference"
            rules={{
              required: true,
              validate: (v) => !!stripReferenceHash(v),
            }}
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                addonBefore="#"
                value={stripReferenceHash(field.value)}
                onChange={(e) =>
                  field.onChange(stripReferenceHash(e.target.value))
                }
              />
            )}
          />
        </Form.Item>

        <Form.Item
          label={
            <Fragment>
              {intl.formatMessage({ id: 'createTenderModal-title' })}{' '}
              <IconPopup translationKey="title" />
            </Fragment>
          }
          validateStatus={errors.title ? 'error' : ''}
          help={
            errors.title &&
            intl.formatMessage({ id: 'createTenderModal-field-required' })
          }>
          <Controller
            name="title"
            rules={{ required: true }}
            control={control}
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>

        <Form.Item
          label={
            <Fragment>
              {intl.formatMessage({ id: 'createTenderModal-public-title' })}{' '}
              <IconPopup translationKey="publicTitle" />
            </Fragment>
          }>
          <Controller
            name="share_title"
            control={control}
            render={({ field }) => <Input {...field} />}
          />
        </Form.Item>

        <Form.Item
          label={
            <Fragment>
              {intl.formatMessage({ id: 'createTenderModal-description' })}{' '}
              <IconPopup translationKey="description" />
            </Fragment>
          }>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <div css={descriptionTextareaWrapperStyle}>
                <TextArea
                  {...field}
                  className="create-tender-description-textarea"
                  maxLength={10000}
                  rows={5}
                  style={{ resize: 'vertical' }}
                />
                <span
                  style={{
                    position: 'absolute',
                    bottom: 5,
                    right: 24,
                    fontSize: 16,
                    color: '#888',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    lineHeight: 1,
                  }}>
                  ↕
                </span>
              </div>
            )}
          />
        </Form.Item>

        <div css={styles.mergeContainer}>
          <div css={styles.twoColumnsField}>
            <Form.Item
              label={
                <Fragment>
                  {intl.formatMessage({ id: 'createTenderModal-deadline' })}{' '}
                  <IconPopup translationKey="deadline" />
                </Fragment>
              }
              validateStatus={errors.deadline_at ? 'error' : ''}
              help={
                errors.deadline_at &&
                intl.formatMessage({ id: 'createTenderModal-field-required' })
              }>
              <Controller
                name="deadline_at"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    format="DD/MM/YYYY - HH:mm"
                    disabledDate={disabledDate}
                    showTime
                    value={field.value}
                    allowClear={false}
                    onChange={field.onChange}
                    style={{ width: '100%' }}
                  />
                )}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

const descriptionTextareaWrapperStyle = css`
  position: relative;

  .create-tender-description-textarea {
    scrollbar-width: auto;
  }

  .create-tender-description-textarea::-webkit-scrollbar {
    width: 16px;
    height: 16px;
  }

  .create-tender-description-textarea::-webkit-scrollbar-track {
    background: transparent;
  }

  .create-tender-description-textarea::-webkit-scrollbar-thumb {
    background-color: rgba(42, 49, 79, 0.65);
    border: 5px solid transparent;
    border-radius: 999px;
    background-clip: content-box;
  }

  .create-tender-description-textarea::-webkit-scrollbar-thumb:hover {
    background-color: rgba(42, 49, 79, 0.8);
  }
`;
